import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const studio = axios.create({ baseURL: `${API}/studio` });

studio.interceptors.request.use((config) => {
  const token = localStorage.getItem("devisto_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const apiClient = axios.create({ baseURL: API });

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("devisto_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const saveSession = (data) => {
  localStorage.setItem("devisto_token", data.token);
  localStorage.setItem("devisto_role", data.role);
  localStorage.setItem("devisto_name", data.name || "");
  localStorage.setItem("devisto_token_at", String(Date.now()));
  if (data.media_token) localStorage.setItem("devisto_media_token", data.media_token);
  if (data.member_role) localStorage.setItem("devisto_member_role", data.member_role);
};

export const isAccountant = () => localStorage.getItem("devisto_member_role") === "comptable";

const tokenExp = (t) => { try { return JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).exp * 1000; } catch { return 0; } };

export const ensureMediaToken = () => {
  const mt = localStorage.getItem("devisto_media_token");
  if (!localStorage.getItem("devisto_token") || (mt && tokenExp(mt) > Date.now() + 24 * 3600 * 1000)) return Promise.resolve();
  return apiClient.get("/media/token").then(({ data }) => localStorage.setItem("devisto_media_token", data.media_token)).catch(() => {});
};

let refreshing = null;
export const refreshSession = () => {
  const token = localStorage.getItem("devisto_token");
  if (!token) return Promise.reject(new Error("no token"));
  if (!refreshing) {
    refreshing = axios.post(`${API}/auth/refresh`, null, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { saveSession(data); return data.token; })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
};

export const sessionAge = () => Date.now() - Number(localStorage.getItem("devisto_token_at") || 0);

const expireSession = () => {
  if (!localStorage.getItem("devisto_token")) return;
  ["devisto_token", "devisto_role", "devisto_name", "devisto_token_at", "devisto_media_token", "devisto_member_role"].forEach((k) => localStorage.removeItem(k));
  const here = window.location.pathname + window.location.search;
  window.location.assign(`/connexion?expire=1&redirect=${encodeURIComponent(here)}`);
};

const onAuthError = async (error) => {
  const { response, config } = error;
  if (response?.status !== 401 || !config || config._retried || !localStorage.getItem("devisto_token")) throw error;
  const detail = response.data?.detail || "";
  if (!/expir|invalide|authentifi/i.test(detail)) throw error;
  try {
    const token = await refreshSession();
    config._retried = true;
    config.headers.Authorization = `Bearer ${token}`;
    return axios(config);
  } catch {
    expireSession();
    throw error;
  }
};
studio.interceptors.response.use((r) => r, onAuthError);
apiClient.interceptors.response.use((r) => r, onAuthError);

export const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("devisto_token")}` });

export const beDate = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");

const CURRENCY_LOCALES = { EUR: "fr-BE" };
const FMT_CACHE = {};
let currentCurrency = localStorage.getItem("devisto_currency") || "EUR";
export const setCurrency = (code) => {
  currentCurrency = CURRENCY_LOCALES[code] ? code : "EUR";
  localStorage.setItem("devisto_currency", currentCurrency);
};
export const getCurrency = () => currentCurrency;
export const money = (v, code) => {
  const cur = CURRENCY_LOCALES[code] ? code : currentCurrency;
  FMT_CACHE[cur] = FMT_CACHE[cur] || new Intl.NumberFormat(CURRENCY_LOCALES[cur], { style: "currency", currency: cur, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return FMT_CACHE[cur].format(Number(v || 0));
};
/* Devise de l'organisation (ou du document public chargé) : plus jamais « € » en dur. */
export const eur = (v) => money(v);

export const apiError = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
};

export const STATUS_OPTIONS = [
  ["nouveau", "Nouveau"],
  ["visite_a_effectuer", "Visite à effectuer"],
  ["en_preparation", "En préparation"],
  ["devis_a_creer", "Devis à créer"],
  ["devis_envoye", "Devis envoyé"],
  ["en_attente", "En attente"],
  ["accepte", "Accepté"],
  ["refuse", "Refusé"],
  ["a_relancer", "À relancer"],
  ["chantier_en_cours", "Chantier en cours"],
  ["chantier_termine", "Chantier terminé"],
  ["archive", "Archivé"],
];

export const STATUS_CLS = {
  nouveau: "bg-slate-100 text-slate-700 border-[#E2E7F7]",
  visite_a_effectuer: "bg-amber-50 text-amber-700 border-amber-200",
  en_preparation: "bg-blue-50 text-blue-700 border-blue-200",
  devis_a_creer: "bg-orange-50 text-orange-700 border-orange-200",
  devis_envoye: "bg-indigo-50 text-indigo-700 border-indigo-200",
  en_attente: "bg-slate-100 text-slate-600 border-[#E2E7F7]",
  accepte: "bg-green-50 text-green-700 border-green-200",
  refuse: "bg-red-50 text-red-600 border-red-200",
  chantier_en_cours: "bg-blue-50 text-blue-700 border-blue-200",
  a_relancer: "bg-amber-50 text-amber-700 border-amber-200",
  chantier_termine: "bg-green-50 text-green-700 border-green-200",
  archive: "bg-slate-100 text-slate-400 border-[#E2E7F7]",
};

export const VAT_COUNTRIES = [
  ["BE", "Belgique"],
  ["FR", "France"],
  ["ES", "Espagne"],
  ["LU", "Luxembourg"],
];

export const VAT_RATES = {
  BE: [0, 6, 12, 21],
  FR: [0, 2.1, 5.5, 10, 20],
  ES: [0, 4, 10, 21],
  LU: [0, 3, 8, 14, 17],
};

export const PAYMENT_TERMS = [
  ["reception", "À réception de facture"],
  ["7", "7 jours"],
  ["15", "15 jours"],
  ["30", "30 jours"],
];

export const TRADE_OPTIONS = [
  ["entrepreneur_general", "Entrepreneur général (tous corps de métier)"],
  ["renovation", "Rénovation générale"],
  ["chauffagiste", "Chauffagiste"],
  ["plombier", "Plombier / Sanitaire"],
  ["electricien", "Électricien"],
  ["climaticien", "Climatisation / PAC"],
  ["ventilation", "Ventilation"],
  ["frigoriste", "Frigoriste"],
  ["traitement_eau", "Traitement de l'eau"],
  ["panneaux_solaires", "Panneaux solaires / photovoltaïque"],
  ["domotique", "Domotique, alarme et caméras"],
  ["ramoneur", "Ramonage et conduits de fumée"],
  ["macon", "Maçon / gros œuvre"],
  ["terrassier", "Terrassement et égouttage"],
  ["demolition", "Démolition"],
  ["beton", "Béton et fondations"],
  ["couvreur", "Couvreur / toiture"],
  ["charpentier", "Charpentier"],
  ["zingueur", "Zinguerie et corniches"],
  ["facadier", "Façadier / crépi et enduits"],
  ["isolation", "Isolation"],
  ["plafonneur", "Plafonneur / enduits intérieurs"],
  ["plaquiste", "Plaquiste / cloisons et faux plafonds"],
  ["chapiste", "Chapiste"],
  ["carreleur", "Carreleur"],
  ["parqueteur", "Parquet et sols souples"],
  ["peintre", "Peintre en bâtiment"],
  ["tapissier", "Tapissier / décoration murale"],
  ["menuisier", "Menuisier intérieur"],
  ["menuisier_ext", "Menuiserie extérieure (châssis, portes)"],
  ["ebeniste", "Ébéniste"],
  ["cuisiniste", "Cuisiniste / salle de bains"],
  ["escalier", "Escaliers"],
  ["vitrier", "Vitrier"],
  ["serrurier", "Serrurier / métallier"],
  ["ferronnier", "Ferronnerie et soudure"],
  ["stores", "Stores, volets et portes de garage"],
  ["ascensoriste", "Ascenseurs et monte-charges"],
  ["desamiantage", "Désamiantage"],
  ["humidite", "Traitement de l'humidité et des caves"],
  ["traitement_bois", "Traitement du bois et de la charpente"],
  ["piscine", "Piscines et spas"],
  ["jardinier", "Jardinier / paysagiste"],
  ["terrasse", "Terrasses et aménagements extérieurs"],
  ["pavage", "Pavage et allées"],
  ["cloture", "Clôtures et portails"],
  ["nettoyage", "Nettoyage et fin de chantier"],
  ["multiservices", "Multiservices / petits travaux"],
  ["autre", "Autre métier technique"],
];

export const JOB_TYPES = {
  chauffagiste: [
    ["remplacement_chaudiere", "Remplacement de chaudière"],
    ["entretien", "Entretien"],
    ["pompe_a_chaleur", "Pompe à chaleur / clim"],
    ["sanitaire", "Sanitaire / eau chaude"],
    ["depannage", "Dépannage"],
    ["autre", "Autre"],
  ],
  plombier: [
    ["sanitaire", "Sanitaire / eau chaude"],
    ["depannage", "Dépannage"],
    ["autre", "Autre"],
  ],
  electricien: [
    ["remplacement_tableau", "Remplacement de tableau"],
    ["mise_en_conformite", "Mise en conformité"],
    ["borne_recharge", "Borne de recharge"],
    ["nouvelle_installation", "Nouvelle installation"],
    ["depannage", "Dépannage"],
    ["autre", "Autre"],
  ],
  climaticien: [
    ["pompe_a_chaleur", "Pompe à chaleur / clim"],
    ["entretien", "Entretien"],
    ["autre", "Autre"],
  ],
  ventilation: [["nouvelle_installation", "Nouvelle installation"], ["entretien", "Entretien"], ["autre", "Autre"]],
  entrepreneur_general: [
    ["renovation_complete", "Rénovation complète"],
    ["gros_oeuvre", "Gros œuvre"],
    ["transformation", "Transformation / extension"],
    ["nouvelle_construction", "Nouvelle construction"],
    ["finitions", "Finitions"],
    ["expertise", "Visite et conseil"],
    ["autre", "Autre"],
  ],
  autre: [
    ["nouvelle_installation", "Nouvelle installation"],
    ["renovation", "Rénovation"],
    ["pose", "Pose / fourniture et pose"],
    ["reparation", "Réparation"],
    ["entretien", "Entretien"],
    ["depannage", "Dépannage"],
    ["expertise", "Visite et conseil"],
    ["autre", "Autre"],
  ],
};

export const PHOTO_CATEGORIES = [
  ["avant", "Avant travaux"],
  ["installation", "Installation existante"],
  ["plaque", "Plaque signalétique"],
  ["mesures", "Mesures"],
  ["probleme", "Problème constaté"],
  ["materiel", "Matériel"],
  ["apres", "Après travaux"],
  ["autre", "Autre"],
];

export const inputCls =
  "w-full rounded-xl border border-[#E2E7F7] bg-white px-3.5 py-2.5 text-[15px] text-[#0F2645] placeholder:text-[#93A0BC] hover:border-[#C9D4F4] focus:border-[#3A5CC8] focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]/15 disabled:bg-[#F6F8FD] disabled:text-[#5C6A87] transition-colors duration-150";

export const btnPrimary =
  "inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl bg-[#3A5CC8] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_1px_2px_rgba(15,38,69,0.14)] hover:bg-[#2F4CA8] hover:shadow-[0_6px_16px_-6px_rgba(58,92,200,0.55)] active:bg-[#243E8B] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A5CC8] focus-visible:ring-offset-2 disabled:opacity-40 disabled:pointer-events-none transition-[background-color,box-shadow,transform] duration-150";

export const btnGhost =
  "inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl border border-[#E2E7F7] bg-white px-4 py-2.5 text-sm font-semibold text-[#0F2645] shadow-[0_1px_2px_rgba(15,38,69,0.06)] hover:border-[#C9D4F4] hover:bg-[#F6F8FD] active:bg-[#ECEFF9] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A5CC8] focus-visible:ring-offset-2 disabled:opacity-40 disabled:pointer-events-none transition-[background-color,border-color,transform] duration-150";

export const btnDanger =
  "inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700 hover:border-rose-300 hover:bg-rose-100 active:bg-rose-200 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 disabled:opacity-40 disabled:pointer-events-none transition-[background-color,transform] duration-150";

export const btnIcon =
  "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E2E7F7] bg-white text-[#0F2645] shadow-[0_1px_2px_rgba(15,38,69,0.06)] hover:border-[#C9D4F4] hover:bg-[#F6F8FD] active:bg-[#ECEFF9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A5CC8] disabled:opacity-40 disabled:pointer-events-none transition-colors duration-150";

export const sym = () => "€";
